import React, { useCallback } from "react";
import { Row, Col, Skeleton, Alert, Spin } from "antd";
import { useTranslation } from "react-i18next";

import { useDisplayMapList } from "../hooks/useDisplayMapList";
import DisplayMapCard from "./DisplayMapCard";

function DisplayMapList({
  // data props (forwarded to the hook)
  displaysMap,
  displaysMapMode,
  displays,
  getDisplays,
  date_from,
  date_to,
  client,
  dsp,
  currency,
  programmatic,
  editCampaignActive,
  campaignCurrency,
  campaignInvoiceIssuingCountry,
  invoiceIssuingCountry,
  isGuaranteed,
  // render-only props (state shared with the parent map)
  user,
  countries,
  setCountries,
  fetchingDisplaysMap,
  currentDisplay,
  onOpenDisplay,
  onHoverDisplay,
}) {
  const { t } = useTranslation();
  const {
    listContentRef,
    loadedDisplays,
    loadingMore,
    displaysIds,
    handleListScroll,
  } = useDisplayMapList({
    displaysMap,
    displaysMapMode,
    displays,
    getDisplays,
    date_from,
    date_to,
    client,
    dsp,
    currency,
    programmatic,
    editCampaignActive,
    campaignCurrency,
    campaignInvoiceIssuingCountry,
    invoiceIssuingCountry,
    isGuaranteed,
  });

  const handleClearCountries = useCallback(
    () => setCountries([]),
    [setCountries],
  );

  const showInitialSkeleton =
    fetchingDisplaysMap || (loadingMore && loadedDisplays.length === 0);

  return (
    <div
      ref={listContentRef}
      className="listContent"
      style={{ marginTop: "0", padding: "0 12px" }}
      onScroll={handleListScroll}
    >
      {showInitialSkeleton ? (
        <div>
          <Row gutter={16}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <Col key={i} xs={24} sm={24} md={24} lg={24} xl={24}>
                <Skeleton active avatar paragraph={{ rows: 4 }} />
              </Col>
            ))}
          </Row>
        </div>
      ) : (
        <>
          {displaysIds.length === 0 && countries.length > 0 && (
            <Alert
              style={{
                width: "96%",
                position: "absolute",
                zIndex: 1,
              }}
              message={
                <div>
                  {t("Results are filtered by country. You can")}{" "}
                  <a onClick={handleClearCountries}>
                    {t("remove that filter and try again.")}
                  </a>
                </div>
              }
              type="warning"
              showIcon
            />
          )}
          <Row className="cardList">
            {loadedDisplays.map((display) => (
              <DisplayMapCard
                key={display.id}
                display={display}
                user={user}
                isCurrent={
                  !!(currentDisplay && currentDisplay.id === display.id)
                }
                onOpenDisplay={onOpenDisplay}
                onHoverDisplay={onHoverDisplay}
              />
            ))}
            {loadingMore && loadedDisplays.length > 0 && (
              <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                <div style={{ textAlign: "center", padding: "12px" }}>
                  <Spin />
                </div>
              </Col>
            )}
          </Row>
        </>
      )}
    </div>
  );
}

// Memoized so changes in the parent map (e.g. hoverDisplay state) don't
// cascade into the list when none of its inputs actually changed.
export default React.memo(DisplayMapList);
